import { useCallback, useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '@/App';
import api from '@/lib/api';
import { adminSurface } from '@/lib/adminSurfaces';

/**
 * The quiet "only you can reach this" marker.
 *
 * Rendered ONLY in an admin session: nobody else is told that a control they
 * cannot see exists. Deliberately almost invisible — a small lock at 60%
 * opacity, no colour, no badge, no extra row height. Legible when looked for,
 * silent when not.
 *
 * Put `data-admin-only` on the element you are marking (the nav row, the card,
 * the button). With "Show what's admin-only" on, one global CSS rule outlines
 * every one of them at once — see index.css.
 */
const HIGHLIGHT_ATTR = 'data-admin-highlight';

export function applyAdminHighlight(on) {
  if (typeof document === 'undefined') return;
  if (on) document.body.setAttribute(HIGHLIGHT_ATTR, 'on');
  else document.body.removeAttribute(HIGHLIGHT_ATTR);
}

/** The switch itself: remembered per admin (on their user record), off by default. */
export function useAdminHighlight() {
  const { user, setUser } = useAuth();
  const isAdmin = (user?.role || '') === 'admin';
  const [on, setOn] = useState(!!user?.admin_highlight);

  useEffect(() => {
    if (!isAdmin) { applyAdminHighlight(false); return; }
    setOn(!!user?.admin_highlight);
    applyAdminHighlight(!!user?.admin_highlight);
  }, [isAdmin, user?.admin_highlight]);

  const toggle = useCallback(async () => {
    const next = !on;
    setOn(next);
    applyAdminHighlight(next);
    try {
      await api.put('/auth/ui/admin-highlight', { on: next });
      setUser?.((prev) => (prev ? { ...prev, admin_highlight: next } : prev));
    } catch {
      /* the outline is already on screen; the preference just didn't stick */
    }
  }, [on, setUser]);

  return { isAdmin, on, toggle };
}

export function AdminOnlyMark({ id, className = '', label = 'Admin only' }) {
  const { user } = useAuth();
  if ((user?.role || '') !== 'admin') return null;
  const surface = id ? adminSurface(id) : null;
  return (
    <span
      title={surface ? `Admin only — ${surface.what}` : label}
      aria-label={label}
      className={`inline-flex items-center shrink-0 opacity-60 text-ink-3 ${className}`}
      data-testid={id ? `admin-only-mark-${id}` : 'admin-only-mark'}
    >
      <Lock className="w-3 h-3" />
    </span>
  );
}

/** Header variant: the same fact said in words, in the same grey as meta text. */
export function AdminOnlyChip({ id, className = '' }) {
  const { user } = useAuth();
  if ((user?.role || '') !== 'admin') return null;
  const surface = id ? adminSurface(id) : null;
  return (
    <span
      title={surface ? surface.what : 'Only an admin can reach this'}
      className={`inline-flex items-center gap-1 text-caption text-ink-3 ${className}`}
      data-testid={id ? `admin-only-chip-${id}` : 'admin-only-chip'}
    >
      <Lock className="w-3 h-3 opacity-60" /> Admin only
    </span>
  );
}

export default AdminOnlyMark;
